import { TvSource, TvSourceTelemetry } from '@reliance/types/models/Tv';
import React from 'react';
import Badge from 'react-bootstrap/Badge';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { formatBoolYesNo } from '../format/BoolYesNo';
import { formatDecimal } from '../format/Decimal';
import { formatUptime } from '../format/Duration';

type TelemetryPanelProps = {
  source: TvSource;
};

type MetricRowProps = {
  label: string;
  value: React.ReactNode;
};

const MetricRow: React.FC<MetricRowProps> = ({ label, value }) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return (
    <div className="d-flex justify-content-between gap-2 py-1 border-bottom">
      <span className="text-muted text-nowrap">{label}</span>
      <span className="text-end text-break" style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
        {value}
      </span>
    </div>
  );
};

const healthBadgeBg = (status: string | null | undefined): string => {
  switch (status) {
    case 'ok':
      return 'success';
    case 'degraded':
      return 'warning';
    case 'critical':
      return 'danger';
    default:
      return 'secondary';
  }
};

const TelemetryPanel: React.FC<TelemetryPanelProps> = ({ source }) => {
  const report: TvSourceTelemetry | null = source.telemetry;
  const hasAny = report != null || source.telemetryStatus != null || source.deviceVersion != null;

  if (!hasAny) {
    return (
      <div className="mt-3 pt-2 border-top">
        <div className="fw-semibold mb-1">Telemetry</div>
        <span className="text-muted">No telemetry reported yet</span>
      </div>
    );
  }

  const stream = report?.stream;
  const audio = report?.audio;
  const network = report?.network;
  const system = report?.system;
  const status = source.telemetryStatus ?? report?.status ?? null;

  return (
    <div className="mt-3 pt-2 border-top">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <div className="fw-semibold">Telemetry</div>
        {status != null && (
          <Badge bg={healthBadgeBg(status)} text={status === 'degraded' ? 'dark' : undefined}>
            {status}
          </Badge>
        )}
        {source.deviceVersion != null && source.deviceVersion !== '' && (
          <span className="text-muted small">| v{source.deviceVersion}</span>
        )}
        {report?.configVersion != null && (
          <span className="text-muted small">| installed config v{report.configVersion}</span>
        )}
      </div>

      <Row className="g-3">
        {stream != null && (
          <Col md={6} lg={3}>
            <div className="text-muted small mb-1">Stream</div>
            <MetricRow label="Healthy" value={formatBoolYesNo(stream.healthy)} />
            <MetricRow label="Service active" value={formatBoolYesNo(stream.serviceActive)} />
            <MetricRow label="Uptime" value={formatUptime(stream.uptimeSecs)} />
            <MetricRow label="Packets/sec" value={formatDecimal(stream.packetsPerSec)} />
            <MetricRow label="Peak dBFS" value={formatDecimal(stream.peakDbfs)} />
            <MetricRow label="Clip ppm" value={stream.clipPpm} />
            <MetricRow label="Underruns" value={stream.underruns} />
            <MetricRow label="Overruns" value={stream.overruns} />
            <MetricRow label="Send errors" value={stream.sendErrors} />
            <MetricRow label="Send error active" value={formatBoolYesNo(stream.sendErrorActive)} />
            <MetricRow label="Capture gain %" value={stream.captureGainPct} />
            <MetricRow label="Status age" value={formatUptime(stream.statusAgeSecs)} />
          </Col>
        )}

        {audio != null && (
          <Col md={6} lg={3}>
            <div className="text-muted small mb-1">Audio</div>
            <MetricRow label="Device" value={audio.configuredDevice} />
            {(audio.inputs ?? []).map((input, index) => (
              <MetricRow
                key={`${input.device ?? 'input'}-${index}`}
                label={input.name ?? input.device ?? `Input ${index + 1}`}
                value={
                  [
                    input.device,
                    input.peakDbfs != null ? `${formatDecimal(input.peakDbfs)} dBFS` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'
                }
              />
            ))}
          </Col>
        )}

        {network != null && (
          <Col md={6} lg={3}>
            <div className="text-muted small mb-1">Network</div>
            <MetricRow label="Interface" value={network.iface} />
            <MetricRow label="Link" value={network.link} />
            <MetricRow label="SSID" value={network.wifi?.ssid} />
            <MetricRow
              label="Signal"
              value={network.wifi?.signalDbm != null ? `${network.wifi.signalDbm} dBm` : null}
            />
            <MetricRow
              label="Frequency"
              value={
                network.wifi?.frequencyMhz != null ? `${network.wifi.frequencyMhz} MHz` : null
              }
            />
            <MetricRow
              label="TX bitrate"
              value={
                network.wifi?.txBitrateMbps != null
                  ? `${formatDecimal(network.wifi.txBitrateMbps)} Mbps`
                  : null
              }
            />
            <MetricRow label="WPA state" value={network.wifi?.wpaState} />
          </Col>
        )}

        {system != null && (
          <Col md={6} lg={3}>
            <div className="text-muted small mb-1">System</div>
            <MetricRow label="Load (1m)" value={formatDecimal(system.load1Minute, 2)} />
            <MetricRow
              label="Mem available"
              value={system.memAvailableMb != null ? `${system.memAvailableMb} MB` : null}
            />
            <MetricRow
              label="Disk free"
              value={system.diskRootFreeMb != null ? `${system.diskRootFreeMb} MB` : null}
            />
            <MetricRow
              label="CPU temp"
              value={system.cpuTempC != null ? `${formatDecimal(system.cpuTempC)} °C` : null}
            />
            <MetricRow label="Uptime" value={formatUptime(system.uptimeSecs)} />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default TelemetryPanel;
